import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Share2,
  Instagram,
  Facebook,
  Linkedin,
  CheckCircle,
  AlertTriangle,
  Circle,
  Link2,
  Copy,
  Check,
  Loader2,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getApiUrl } from '../../lib/apiConfig';
import { PageChoice } from '../PageChoice';
import { facebookPageSection, linkedinPageSection, type SocialPage } from '../../lib/pageChoice';

type PlatformKey = 'instagram' | 'facebook' | 'linkedin';

interface ConnectedAccount {
  handle?: string;
  reauth_required?: boolean;
}

type ConnectedPlatforms = Partial<Record<PlatformKey, ConnectedAccount | '' | undefined>>;

const PLATFORMS: { key: PlatformKey; label: string; Icon: typeof Instagram }[] = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram },
  { key: 'facebook', label: 'Facebook', Icon: Facebook },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin },
];

function statusFor(acc: ConnectedAccount | '' | undefined): {
  state: 'connected' | 'reauth' | 'none';
  label: string;
} {
  if (acc && typeof acc === 'object' && acc.handle) {
    return acc.reauth_required
      ? { state: 'reauth', label: `Verbinding verlopen (was @${acc.handle})` }
      : { state: 'connected', label: `Gekoppeld als ${acc.handle}` };
  }
  return { state: 'none', label: 'Niet gekoppeld' };
}

export function AdminUserSocials({ userId }: { userId: string }) {
  const [platforms, setPlatforms] = useState<ConnectedPlatforms>({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [connectUrl, setConnectUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [fbPages, setFbPages] = useState<SocialPage[]>([]);
  const [fbPageId, setFbPageId] = useState<string | null>(null);
  const [fbPageName, setFbPageName] = useState<string | null>(null);
  const [fbSaving, setFbSaving] = useState(false);

  const [liPages, setLiPages] = useState<SocialPage[]>([]);
  const [liPageId, setLiPageId] = useState<string | null>(null);
  const [liPageName, setLiPageName] = useState<string | null>(null);
  const [liSaving, setLiSaving] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Expliciet true zetten: StrictMode draait in dev cleanup + re-setup,
    // maar refs resetten niet mee.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fbConnected = statusFor(platforms.facebook).state === 'connected';
  const liConnected = statusFor(platforms.linkedin).state === 'connected';

  const callFacebookPages = useCallback(
    async (payload: Record<string, unknown>) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return null;
      const res = await fetch(getApiUrl('/functions/v1/facebook-pages'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...payload, target_user_id: userId }),
      });
      // Stil falen bij list: zonder paginalijst blijft het paneel bruikbaar.
      if (!res.ok) return null;
      return res.json();
    },
    [userId],
  );

  useEffect(() => {
    if (!fbConnected) return;
    let cancelled = false;
    callFacebookPages({ action: 'list' }).then((body) => {
      if (cancelled || !body?.success) return;
      setFbPages(body.pages ?? []);
      setFbPageId(body.current_page_id ?? null);
      setFbPageName(body.current_page_name ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [fbConnected, callFacebookPages]);

  const handleSelectPage = async (pageId: string) => {
    setError(null);
    setFbSaving(true);
    const body = await callFacebookPages({ action: 'set', page_id: pageId });
    if (mountedRef.current) {
      if (body?.success) {
        setFbPageId(body.page_id);
        setFbPageName(body.page_name);
      } else {
        setError('De paginakeuze opslaan lukte niet. Probeer het zo nog eens.');
      }
      setFbSaving(false);
    }
  };

  useEffect(() => {
    if (!liConnected) return;
    let cancelled = false;
    callFacebookPages({ action: 'list', platform: 'linkedin' }).then((body) => {
      if (cancelled || !body?.success) return;
      setLiPages(body.pages ?? []);
      setLiPageId(body.current_page_id ?? null);
      setLiPageName(body.current_page_name ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [liConnected, callFacebookPages]);

  const handleSelectLinkedinPage = async (pageId: string | null) => {
    setError(null);
    setLiSaving(true);
    const body = await callFacebookPages({
      action: 'set',
      platform: 'linkedin',
      page_id: pageId,
    });
    if (mountedRef.current) {
      if (body?.success) {
        setLiPageId(body.page_id ?? null);
        setLiPageName(body.page_name ?? null);
      } else {
        setError('De paginakeuze opslaan lukte niet. Probeer het zo nog eens.');
      }
      setLiSaving(false);
    }
  };

  const load = useCallback(async () => {
    const { data } = await supabase
      .from('social_profiles')
      .select('connected_platforms')
      .eq('user_id', userId)
      .maybeSingle();
    setPlatforms((data?.connected_platforms ?? {}) as ConnectedPlatforms);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const generateLink = async () => {
    setError(null);
    setGenerating(true);
    setConnectUrl(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) {
        setError('Sessie verlopen — log opnieuw in.');
        return;
      }
      const res = await fetch(getApiUrl('/functions/v1/upload-post-connect'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ target_user_id: userId }),
      });
      const body = await res.json();
      if (!res.ok || !body.access_url) {
        setError(body.error || 'Kon geen connect-link genereren.');
        return;
      }
      setConnectUrl(body.access_url);
      await load();
    } catch {
      setError('Er ging iets mis bij het genereren van de link.');
    } finally {
      setGenerating(false);
    }
  };

  const copy = async () => {
    if (!connectUrl) return;
    try {
      await navigator.clipboard.writeText(connectUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard kan geblokkeerd zijn; link staat ook in het veld */
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          Social koppelingen
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Gekoppelde social-accounts van deze gebruiker. Bij Facebook en
          LinkedIn kun je instellen waar geplaatst wordt.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-3 mb-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            Status laden...
          </div>
        ) : (
          PLATFORMS.map(({ key, label, Icon }) => {
            const s = statusFor(platforms[key]);
            return (
              <div key={key} className="rounded-lg border border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3 p-3">
                  <Icon className="w-5 h-5 text-gray-700 shrink-0" />
                  <span className="font-medium text-gray-900 w-24 shrink-0">{label}</span>
                  <span className="flex items-center gap-2 text-sm">
                    {s.state === 'connected' && (
                      <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    )}
                    {s.state === 'reauth' && (
                      <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    )}
                    {s.state === 'none' && (
                      <Circle className="w-4 h-4 text-gray-300 shrink-0" />
                    )}
                    <span
                      className={
                        s.state === 'connected'
                          ? 'text-green-700'
                          : s.state === 'reauth'
                            ? 'text-amber-700'
                            : 'text-gray-500'
                      }
                    >
                      {s.label}
                    </span>
                  </span>
                </div>
                {key === 'facebook' && (
                  <PageChoice
                    section={facebookPageSection(fbConnected, fbPages.length, fbPageId)}
                    pages={fbPages}
                    currentPageId={fbPageId}
                    currentPageName={fbPageName}
                    saving={fbSaving}
                    onSelect={(pageId) => {
                      if (pageId) handleSelectPage(pageId);
                    }}
                    label="Op welke Facebook-pagina wil je posten?"
                  />
                )}
                {key === 'linkedin' && (
                  <PageChoice
                    section={linkedinPageSection(liConnected, liPages.length)}
                    pages={liPages}
                    currentPageId={liPageId}
                    currentPageName={liPageName}
                    saving={liSaving}
                    onSelect={handleSelectLinkedinPage}
                    label="Waar wil je op LinkedIn posten?"
                    defaultOptionLabel="Persoonlijk profiel"
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="text-sm text-gray-600 mb-3">
          Connect-link genereren om naar deze gebruiker te sturen (support):
        </p>
        <button
          onClick={generateLink}
          disabled={generating}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#231F20] text-white rounded-lg hover:bg-[#231F20]/90 transition-colors text-sm font-medium disabled:opacity-50"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Bezig...
            </>
          ) : (
            <>
              <Link2 className="w-4 h-4" />
              Connect-link genereren
            </>
          )}
        </button>

        {connectUrl && (
          <div className="mt-3 flex items-stretch gap-2">
            <input
              readOnly
              value={connectUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg bg-gray-50 font-mono"
            />
            <button
              onClick={copy}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-green-600" /> Gekopieerd
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" /> Kopieer
                </>
              )}
            </button>
          </div>
        )}
        {connectUrl && (
          <p className="text-xs text-gray-400 mt-2">
            Deze link is 48 uur geldig. De gebruiker koppelt z'n socials op een
            beveiligde pagina; jij hoeft verder niets te doen.
          </p>
        )}
      </div>
    </div>
  );
}
